import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import type {UserSettingsOptionConfig} from '@github-ui/issue-create/getSafeConfig'
import {noop} from '@github-ui/noop'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useNavigate} from '@github-ui/use-navigate'
import {Button} from '@primer/react'
import {useCallback} from 'react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import type {MilestoneActions$key} from './__generated__/MilestoneActions.graphql'
import {LABELS} from './constants/labels'
import {MilestoneTitle} from './MilestoneTitle'
import {ToggleMilestoneState} from './ToggleMilestoneState'

type MilestoneActionsProps = {
  repositoryRef: MilestoneActions$key
  optionConfig?: UserSettingsOptionConfig
}

export function MilestoneActions({repositoryRef, optionConfig}: MilestoneActionsProps) {
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const {milestone, viewerCanPush, owner, name} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment MilestoneActions on Repository @argumentDefinitions(number: {type: "Int!"}) {
        milestone(number: $number) {
          ...ToggleMilestoneState
          ...MilestoneTitle
          number
          title
        }
        viewerCanPush
        owner {
          login
        }
        name
      }
    `,
    repositoryRef,
  )
  const navigate = useNavigate()

  const navigateToMilestoneFn = useCallback(
    (url: string) => {
      if (!milestone || !milestone.title) return
      const milestoneParam = `milestone=${encodeURIComponent(milestone.title)}`
      const urlWithMilestone = url.includes('?') ? `${url}&${milestoneParam}` : `${url}?${milestoneParam}`
      navigate(urlWithMilestone)
    },
    [milestone, navigate],
  )

  if (sidebarLayoutEnabled || !milestone) return null

  const editMilestoneUrl = `/${owner.login}/${name}/milestones/${milestone.number}/edit`

  return (
    <MilestoneTitle milestoneRef={milestone}>
      {viewerCanPush ? (
        <>
          <Button as={Link} to={editMilestoneUrl}>
            {LABELS.editMilestone}
          </Button>

          <ToggleMilestoneState milestoneRef={milestone} />
        </>
      ) : null}
      <CreateIssueButton
        label={LABELS.newIssue}
        navigate={navigateToMilestoneFn}
        optionConfig={{
          ...optionConfig,
          showRepositoryPicker: false,
          issueCreateArguments: {
            repository: {
              owner: owner.login,
              name,
            },
          },
          showFullScreenButton: true,
          navigateToFullScreenOnTemplateChoice: navigate !== noop,
          canBypassTemplateSelection: true,
        }}
      />
    </MilestoneTitle>
  )
}
